import React, { useState } from 'react';
import { useOffline } from '../contexts/OfflineContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { PremiumBadge } from './PremiumGate';
import DownloadProgressModal from './DownloadProgressModal';
import RemoveDownloadModal from './RemoveDownloadModal';
import './DownloadListButton.css';

/**
 * Download button for saving a list for offline access
 * Shows download/downloaded/downloading states with modals
 */
function DownloadListButton({ listId, listTitle, className = '' }) {
  const { canAccess } = useSubscription();
  const {
    isListDownloaded,
    isListDownloading,
    getDownloadProgress,
    downloadList,
    cancelDownload,
    removeOfflineList,
  } = useOffline();

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState(null);

  const canOfflineDownload = canAccess('offlineDownload');
  const isDownloaded = isListDownloaded(listId);
  const isDownloading = isListDownloading(listId);
  const progress = getDownloadProgress(listId);

  const handleClick = async () => {
    if (!canOfflineDownload) {
      return; // Premium gate will show
    }

    if (isDownloading) {
      return; // Already downloading
    }

    setError(null);

    if (isDownloaded) {
      // Show confirmation modal
      setShowRemoveModal(true);
    } else {
      // Start download - modal will appear automatically via progress state
      try {
        await downloadList(listId);
      } catch (err) {
        setError('Download failed');
        console.error('Failed to download list:', err);
      }
    }
  };

  const handleCancelDownload = () => {
    cancelDownload(listId);
  };

  const handleConfirmRemove = async () => {
    setIsRemoving(true);
    try {
      await removeOfflineList(listId);
      setShowRemoveModal(false);
    } catch (err) {
      console.error('Failed to remove offline list:', err);
    } finally {
      setIsRemoving(false);
    }
  };

  const getTitle = () => {
    if (!canOfflineDownload) return 'Premium feature';
    if (isDownloading) return 'Downloading...';
    if (isDownloaded) return 'Downloaded - tap to remove';
    return 'Download for offline';
  };

  return (
    <>
      <div className={`download-list-btn-wrapper ${className}`}>
        <button
          className={`btn-icon-header download-list-btn ${isDownloaded ? 'downloaded' : ''} ${isDownloading ? 'downloading' : ''} ${!canOfflineDownload ? 'premium-locked' : ''}`}
          onClick={handleClick}
          disabled={isDownloading}
          title={getTitle()}
          aria-label={getTitle()}
        >
          {isDownloading ? (
            <DownloadingIcon />
          ) : isDownloaded ? (
            <DownloadedIcon />
          ) : (
            <DownloadIcon />
          )}
          {!canOfflineDownload && <PremiumBadge small />}
        </button>

        {error && <span className="download-error-text">{error}</span>}
      </div>

      {/* Download progress modal */}
      <DownloadProgressModal
        isOpen={isDownloading}
        listTitle={listTitle || 'List'}
        phase={progress?.phase}
        current={progress?.current || 0}
        total={progress?.total || 0}
        onCancel={handleCancelDownload}
      />

      {/* Remove confirmation modal */}
      <RemoveDownloadModal
        isOpen={showRemoveModal}
        listTitle={listTitle || 'List'}
        onConfirm={handleConfirmRemove}
        onCancel={() => setShowRemoveModal(false)}
        isRemoving={isRemoving}
      />
    </>
  );
}

// Download icon (arrow pointing down into tray)
const DownloadIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Downloaded icon (checkmark in tray)
const DownloadedIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="9 11 12 14 22 4" />
  </svg>
);

// Downloading icon (animated spinner)
const DownloadingIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="downloading-spinner"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <circle cx="12" cy="8" r="4" strokeDasharray="20" strokeDashoffset="5" />
  </svg>
);

export default DownloadListButton;
